import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * One shipping address in the Details modal, as an accordion.
 *
 * A bundle has no address of its own — the source view dropped its whole
 * address block on 2026-08-19 — so the address comes from the linked WORK
 * ORDER. Two consequences show up directly in this component:
 *
 *   * an UNMAPPED bundle has no work order and therefore no address, so those
 *     land in an explicit "no work order linked" group rather than under a
 *     blank heading;
 *   * a work order whose shippingAddress is empty gets its own "no shipping
 *     address" group, which is a different problem from not being mapped and
 *     is worth telling apart on screen.
 *
 * Inside a group, bundles of the same order collapse to ONE row carrying the
 * count — an order routinely has 19 bundles that differ only by bundle
 * number, and listing them individually is what made the old flat table
 * unreadable.
 */

const addressLines = (a) => {
  if (!a) return [];
  const cityLine = [a.city, a.state, a.zip].filter(Boolean).join(" ");
  return [
    a.addressLine1,
    a.addressLine2,
    cityLine,
    // Country only when it is not the default — on a US-only invoice a
    // repeated "US" on every group is noise.
    a.country && a.country.toUpperCase() !== "US" ? a.country : "",
  ].filter((l) => l && String(l).trim() !== "");
};

/**
 * "3 Sep" for a validation timestamp, or "" when there is none.
 *
 * Day and month only: the useful question is how long ago, and a full
 * timestamp on every group is noise in a list of fifty. The full value is in
 * the button's title attribute for anyone who wants it.
 */
const formatValidatedAt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

// Work order due date / in-hand date for the row. Full date with year --
// unlike the validation stamp, the year matters when packing against a
// deadline. "" when the order has no due date set.
const formatDueDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Per-kind styling. The left accent is the fastest read in the list: a real
// address is actionable, the two "nothing to ship to" cases are not, and the
// colour says which before any text is read.
const HEADINGS = {
  no_work_order: {
    title: "No work order linked",
    hint: "These bundles have no W-ID yet, so there is no shipping address to group them by.",
    badge: "bg-warning text-dark",
    accent: "#f7b924",
  },
  no_address: {
    title: "Work order has no shipping address",
    hint: "Linked to a work order, but that work order's shipping address is empty.",
    badge: "bg-secondary",
    accent: "#adb5bd",
  },
};

const ADDRESS_ACCENT = "#3b7ddd";

/**
 * A label over its number, so the header reads as a row of stats.
 *
 * The label sits on the SAME line as the company/contact row beside it, so the
 * two read as one band across the header rather than the stats floating
 * halfway down. That is why the whole block is top-aligned and the label's
 * line-height matches the 16px company name next to it.
 */
const Stat = ({ label, value, tone = "primary" }) => (
  <div className="text-center px-2">
    <div
      className="text-muted text-uppercase fw-semibold"
      style={{ fontSize: 13, letterSpacing: 0.4, lineHeight: "20px" }}
    >
      {label}
    </div>
    <div
      className={`fw-bold text-${tone}`}
      style={{ fontSize: 20, lineHeight: 1.2 }}
    >
      {value}
    </div>
  </div>
);

/**
 * The RFID handheld's last scan of this address group.
 *
 * Its own component rather than another <Stat> because it answers three
 * questions at once — how much of the group was found, what is missing, and
 * what does not belong here — and because the empty state is not a number.
 *
 * NEVER SCANNED IS A DASH, NOT A ZERO. "0/6" would read as a scan that found
 * nothing, which is a fire; "nobody has checked this yet" is not. Those must
 * not look the same on a packing floor.
 *
 * Exported for its test, and so the page can render it in a summary row later
 * without reaching into this file's internals.
 *
 * Two props exist only for the second home it now has, the invoice list on
 * Nova Shipping, and both default to today's address-card behaviour:
 *
 *   * `showLabel` — off in a table, where the column header already says
 *     "Scanned" and repeating it inside every cell is noise;
 *   * `onOpen` — absent in a table, where there is no detail panel to open, so
 *     the block renders as plain text. A button that swallows a press without
 *     answering is worse than no button.
 */
export const ScanStat = ({ lastScan, onOpen, showLabel = true }) => {
  const label = showLabel ? (
    <div
      className="text-muted text-uppercase fw-semibold"
      style={{ fontSize: 13, letterSpacing: 0.4, lineHeight: "20px" }}
    >
      Scanned
    </div>
  ) : null;

  if (!lastScan) {
    return (
      <div
        className="text-center px-2"
        title="Not scanned with the RFID handheld"
      >
        {label}
        <div className="fw-bold text-muted" style={{ fontSize: 20, lineHeight: 1.2 }}>
          —
        </div>
        <div className="text-muted" style={{ fontSize: 11 }}>
          not scanned
        </div>
      </div>
    );
  }

  const found = Math.max(0, lastScan.expectedCount - lastScan.missingCount);
  const clean = lastScan.missingCount === 0 && lastScan.extraCount === 0;
  const problems = [
    lastScan.missingCount ? `${lastScan.missingCount} missing` : null,
    lastScan.extraCount ? `${lastScan.extraCount} extra` : null,
  ].filter(Boolean);

  const Wrapper = onOpen ? "button" : "div";

  return (
    <Wrapper
      {...(onOpen ? { type: "button", onClick: onOpen } : {})}
      className={`text-center px-2 ${onOpen ? "btn btn-sm border-0" : ""}`}
      title={clean ? "All expected bundles scanned" : problems.join(", ")}
    >
      {label}
      <div
        className={`fw-bold ${clean ? "text-success" : "text-danger"}`}
        style={{ fontSize: 20, lineHeight: 1.2 }}
      >
        {found}/{lastScan.expectedCount}
      </div>
      {/* The counts in words, because "4/6" alone does not say whether the two
          that are unaccounted for are missing or sitting in the wrong pile. */}
      <div
        className={clean ? "text-success" : "text-danger"}
        style={{ fontSize: 11 }}
      >
        {clean ? "verified" : problems.join(" · ")}
      </div>
    </Wrapper>
  );
};

const MENU_WIDTH = 260;

/**
 * The columns that did not earn a place in the row, behind a three-dot menu.
 *
 * Rendered in a PORTAL with position:fixed. The row lives inside
 * .table-responsive (overflow-x: auto), and an absolutely-positioned child
 * cannot escape a scroll container — it gets clipped AND widens the scrollable
 * area, which is why opening the menu threw the whole table into horizontal
 * scroll and shoved the rows out of view. A portal to <body> leaves that
 * container behind entirely.
 *
 * Fixed coordinates stop matching the row as soon as anything scrolls, so it
 * closes on scroll and resize as well as on outside click and Escape — a menu
 * that can only be dismissed by clicking its own button is a trap inside a
 * scrolling accordion.
 */
const RowMenu = ({ order, onEditMapping }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Positioned against the button's viewport box, then kept on screen:
  // right-aligned to the button, flipped above when there is no room below.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight || 220;
    const roomBelow = window.innerHeight - r.bottom;
    setPos({
      top:
        roomBelow < height + 8
          ? Math.max(8, r.top - height - 4)
          : r.bottom + 4,
      left: Math.max(
        8,
        Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
      ),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        btnRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Capture phase: the thing that scrolls is an ancestor element, not the
    // document, so a bubbling listener never sees it.
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <div className="d-inline-block">
      <button
        type="button"
        ref={btnRef}
        className="btn btn-sm btn-light border"
        onClick={() => setOpen((v) => !v)}
        title="More details for this order"
        aria-label={`More details for order ${order.order_id}`}
        aria-expanded={open}
      >
        <i className="mdi mdi-dots-vertical" aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-white border rounded shadow p-2 text-start"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
              // Above the details modal it sits in (100001) and below the
              // Link WO modal it opens (100021). Those numbers clear the
              // sidebar's 99999 -- see the stacking note in NovaShipping.jsx.
              zIndex: 100010,
            }}
          >
            <div className="small">
            <div className="d-flex justify-content-between gap-2 mb-1">
              <span className="text-muted">Order</span>
              <span className="fw-semibold">{order.order_id ?? "-"}</span>
            </div>
            <div className="d-flex justify-content-between gap-2 mb-1">
              <span className="text-muted">Description</span>
              <span className="fw-semibold text-end">
                {order.order_description || "-"}
              </span>
            </div>
            <div className="d-flex justify-content-between gap-2 mb-1">
              <span className="text-muted">Company</span>
              <span className="fw-semibold text-end">
                {order.source_company_name || "-"}
                {order.source_company_id ? ` (${order.source_company_id})` : ""}
              </span>
            </div>
            <div className="d-flex justify-content-between gap-2 mb-1">
              <span className="text-muted">ERP location</span>
              <span className="fw-semibold">{order.erpLocationName || "-"}</span>
            </div>
            {order.shippedAt && (
              <div className="d-flex justify-content-between gap-2 mb-1">
                <span className="text-muted">Shipped</span>
                <span className="fw-semibold">
                  {new Date(order.shippedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
            <button
              className="btn btn-sm btn-info w-100 mt-2"
              onClick={() => {
                setOpen(false);
                onEditMapping(order);
              }}
            >
              {order.woId ? "Change work order" : "Link work order"}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default function ShippingAddressGroup({
  group,
  open,
  onToggle,
  onOpenWorkOrder,
  onEditMapping,
  onToggleShipped,
  onShipGroup,
  onValidateGroup,
  onOpenAddressHistory,
  // Clears this group's RFID scan result -- see the Reset button in the scan
  // panel below.
  onResetScan,
  // order_id currently being saved, so its checkbox can't be double-clicked
  // into a race while the request is in flight.
  shippingOrderId,
  // true while the whole group is being marked shipped.
  shippingGroup,
}) {
  // The scan column's detail panel, closed until the column is pressed: the
  // missing/extra ids are only useful once something is wrong.
  const [scanDetailOpen, setScanDetailOpen] = useState(false);

  const lines = addressLines(group.address);
  const meta = HEADINGS[group.kind];
  const shippedCount = group.orders.filter((o) => o.shipped).length;
  const allShipped = shippedCount === group.orderCount;
  // Distinct work orders in this group, which is what the WOs stat counts —
  // orders and work orders are not the same thing (two orders can share one).
  const woCount = new Set(
    group.orders.map((o) => o.woId).filter((n) => Number.isInteger(n)),
  ).size;

  return (
    <div
      className={`border rounded-3 mb-2 shadow-sm overflow-hidden ${
        // A fully shipped address only appears while Show shipped is on, and
        // is greyed there so it reads as done rather than as outstanding work
        // sitting among the groups still to pack.
        allShipped ? "bg-body-secondary" : "bg-white"
      }`}
      style={{
        // Left accent keyed to the group kind -- see HEADINGS. Muted along
        // with the rest of the card once the address is fully shipped.
        borderLeft: `4px solid ${meta ? meta.accent : ADDRESS_ACCENT}`,
        ...(allShipped ? { background: "#f4f5f7", opacity: 0.72 } : null),
      }}
    >
      {/* The header is a row, not a button: it now holds its own Shipped
          button, and a button inside a button is invalid HTML that browsers
          resolve by dropping one of them. The chevron + identity block is the
          clickable part instead, and it is a real <button> so the accordion
          stays keyboard-reachable.
          Only the OPEN header is tinted -- closed ones stay white so the list
          reads as a stack of plain cards. */}
      {/* align-items-start, not center: the stat labels line up with the
          company/contact row rather than floating level with the address
          beneath it. */}
      <div
        className={`d-flex align-items-start gap-2 px-3 py-2 ${
          open ? "bg-primary bg-opacity-10" : "bg-white"
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="btn border-0 bg-transparent text-start d-flex align-items-start gap-2 flex-grow-1 p-0"
          style={{ minWidth: 0 }}
        >
          <i
            className={`mdi ${open ? "mdi-chevron-down" : "mdi-chevron-right"} fs-4 lh-1`}
            aria-hidden="true"
          />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {meta ? (
              <>
                <div className="fw-semibold" style={{ fontSize: 16 }}>
                  {meta.title}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {meta.hint}
                </div>
              </>
            ) : (
              <div className="lh-sm">
                {/* Row 1: the company. */}
                <div className="fw-bold" style={{ fontSize: 16 }}>
                  {group.address.addressCompany ||
                    group.customerName ||
                    "Unnamed company"}
                </div>
                {/* Row 2: the contact, on its own line directly above the
                    address rather than trailing the company name. It reads as
                    part of the destination -- it is who the parcel is
                    attention-to -- so it belongs with the address block. */}
                {(group.contactName || group.address.attentionTo) && (
                  <div className="text-muted" style={{ fontSize: 14 }}>
                    {group.contactName || group.address.attentionTo}
                  </div>
                )}
                {/* Row 3: where. Bigger than the old `small` -- this is the
                    line being checked against a parcel label. */}
                <div className="mt-1" style={{ fontSize: 14 }}>
                  {lines.map((l, i) => (
                    <div key={i} className={i === 0 ? "" : "text-muted"}>
                      {l}
                    </div>
                  ))}
                </div>
                {/* Sits under the address because that is what it is about --
                    not under the company, and not next to the buttons, where
                    it would read as a control. Only when the WHOLE group is
                    validated: a partly-checked group still needs the button
                    pressing, so telling it "validated" would be a lie. */}
                {group.addressChecked && (
                  <div className="mt-1">
                    {group.addressValidated ? (
                      <span
                        className="badge bg-success bg-opacity-10 text-success fw-normal"
                        style={{ fontSize: 12 }}
                      >
                        <i className="mdi mdi-check-circle-outline me-1" aria-hidden="true" />
                        Validated {formatValidatedAt(group.addressValidatedAt)}
                      </span>
                    ) : (
                      // Checked, but UPS could not resolve it. Deliberately NOT
                      // a green tick: this address still needs a human, and a
                      // success badge would bury that. It is still skipped on
                      // the next press -- Re-validate is how you ask again.
                      <span
                        className="badge bg-warning bg-opacity-25 text-dark fw-normal"
                        style={{ fontSize: 12 }}
                      >
                        <i className="mdi mdi-alert-outline me-1" aria-hidden="true" />
                        Checked {formatValidatedAt(group.addressValidatedAt)} —
                        UPS could not resolve
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </button>

        {/* Stats as labelled columns, then the group's own Shipped button.
            Top-aligned so the labels sit on the company/contact line. */}
        <div className="d-flex align-items-start gap-1 text-nowrap">
          <Stat label="Bundles" value={group.bundleCount} />
          <Stat label="WO" value={woCount} tone="dark" />
          <Stat
            label="MO"
            value={
              shippedCount > 0 ? `${shippedCount}/${group.orderCount}` : group.orderCount
            }
            tone={allShipped ? "success" : "dark"}
          />
          {/* What the RFID handheld found the last time this address was
              scanned. Only for real addresses -- a pile of boxes cannot be
              checked against a group that has nowhere to go. */}
          {group.kind === "address" && (
            <ScanStat
              lastScan={group.lastScan}
              onOpen={() => setScanDetailOpen((v) => !v)}
            />
          )}
          {/* Clearing sits BESIDE the number, not inside the panel behind it:
              the packer re-packs the pile and re-scans, and having to open a
              panel first to find the button is a step in the way of the thing
              they are already doing. Only shown when there is a result to
              clear. */}
          {group.kind === "address" && group.lastScan && onResetScan && (
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-0 ms-1 align-self-center"
              title="Clear this scan result so the address can be scanned again"
              onClick={() => {
                if (
                  window.confirm(
                    "Clear the scan result for this address? The column goes back to 'not scanned'. Bundles are not changed.",
                  )
                ) {
                  setScanDetailOpen(false);
                  onResetScan(group);
                }
              }}
            >
              <i
                className="mdi mdi-refresh fs-5"
                aria-hidden="true"
                style={{ lineHeight: 1 }}
              />
              <span className="visually-hidden">Reset scan</span>
            </button>
          )}

          {/* Marks every order in the group shipped in ONE request. Ticking
              nine orders one at a time can half-succeed and leave the group in
              a state nobody chose.
              Shown for EVERY group kind, not just real addresses: the two
              "nothing to ship to" buckets still hold real orders that get
              packed and sent, and they were the ones with no quick way to
              clear them. */}
          <button
            type="button"
            className={`btn btn-sm ms-1 ${
              allShipped ? "btn-outline-success" : "btn-success"
            }`}
            disabled={shippingGroup}
            onClick={() => onShipGroup(group, !allShipped)}
            title={
              allShipped
                ? `Un-mark all ${group.orderCount} order${group.orderCount === 1 ? "" : "s"} and bring them back into the queue`
                : // "at this address" would be a lie on the two grouped-by-
                  // absence buckets, which have no address at all.
                  `Mark all ${group.orderCount} order${group.orderCount === 1 ? "" : "s"} ${
                    meta ? "in this group" : "at this address"
                  } shipped`
            }
          >
            <i
              className={`mdi ${allShipped ? "mdi-undo" : "mdi-truck-check"} me-1`}
              aria-hidden="true"
            />
            {shippingGroup ? "Saving…" : allShipped ? "Undo" : "Shipped"}
          </button>

          {/* Only on real addresses. The two "nothing to ship to" buckets have
              no address to validate, and offering the button there would be a
              dead end.

              Manual by design: one UPS call per work order, so validating
              everything on every load would be thousands of calls for a
              problem that affects a handful of groups. The user points it at
              the group that looks wrong. */}
          {/* Once every order here has a stored result for the address as it
              stands, the button becomes Re-validate and forces a fresh UPS
              call. Without that there would be no way back for an address
              whose stored answer is wrong. The date sits beside it, because
              "validated" with no "when" is not worth much. */}
          {!meta && onValidateGroup && (
            <button
              type="button"
              className={`btn btn-sm ms-1 ${
                group.addressChecked
                  ? "btn-outline-secondary"
                  : "btn-outline-primary"
              }`}
              onClick={() => onValidateGroup(group, { force: !!group.addressChecked })}
              title={
                group.addressChecked
                  ? `Checked against UPS ${formatValidatedAt(group.addressValidatedAt)}. Press to check again.`
                  : "Check this address against UPS and, if it differs, offer to correct the orders so they group together"
              }
            >
              <i
                className={`mdi ${group.addressChecked ? "mdi-refresh" : "mdi-map-marker-check"} me-1`}
                aria-hidden="true"
              />
              {group.addressChecked ? "Re-validate" : "Validate"}
            </button>
          )}
        </div>
      </div>

      {/* Scan detail sits OUTSIDE the accordion: the column that opens it is in
          the header, so hiding this behind the same toggle as the orders table
          would make pressing the column appear to do nothing. */}
      {scanDetailOpen && group.lastScan && (
        <div className="border-top bg-body-secondary px-3 py-2" style={{ fontSize: 13 }}>
          <div className="text-muted mb-1">
            Scanned {formatValidatedAt(group.lastScan.scannedAt)}
            {group.lastScan.deviceId ? ` · ${group.lastScan.deviceId}` : ""} ·{" "}
            {group.lastScan.expectedCount} expected
          </div>
          {group.lastScan.missingIds?.length > 0 && (
            <div className="text-danger">
              <strong>Missing from the last scan:</strong>{" "}
              {group.lastScan.missingIds.join(", ")}
            </div>
          )}
          {group.lastScan.extraIds?.length > 0 && (
            <div className="text-danger">
              <strong>Not expected here:</strong>{" "}
              {group.lastScan.extraIds.join(", ")}
            </div>
          )}
          {group.lastScan.missingCount === 0 &&
            group.lastScan.extraCount === 0 && (
              <div className="text-success">
                Every expected bundle was found, and nothing else was in the pile.
              </div>
            )}
        </div>
      )}

      {open && (
        <div className="border-top">
          <div className="table-responsive">
            {/* table-hover so a row lights up under the cursor -- with several
                near-identical order descriptions it is easy to lose which line
                the menu belongs to. */}
            <table className="table table-sm table-hover mb-0 align-middle">
              <thead className="table-light">
                {/* 13px rather than Bootstrap's `small` (~12.25px): these are
                    column headers on a working screen, not fine print. */}
                <tr
                  className="text-muted text-uppercase fw-semibold"
                  style={{ fontSize: 13 }}
                >
                  <th style={{ width: 90 }} className="text-center">
                    Shipped
                  </th>
                  <th style={{ width: 110 }} className="text-center">
                    W-ID
                  </th>
                  <th>Job Name</th>
                  <th style={{ width: 130 }}>In-hand date</th>
                  <th style={{ width: 150 }}>Shipment Method</th>
                  <th style={{ width: 90 }} className="text-center">
                    Bundles
                  </th>
                  <th style={{ width: 60 }} className="text-end"></th>
                </tr>
              </thead>
              <tbody>
                {group.orders.map((o) => (
                  <tr
                    key={o.order_id}
                    className={`${o.anyDeleted ? "table-secondary text-muted" : ""} ${
                      o.woId ? "cursor-pointer" : ""
                    }`}
                    style={o.woId ? { cursor: "pointer" } : undefined}
                    // The whole row opens the work order's products. The two
                    // interactive cells stop propagation themselves rather
                    // than being excluded by target-sniffing here, which
                    // breaks the moment a control gains an inner element.
                    onClick={o.woId ? () => onOpenWorkOrder(o.woId) : undefined}
                    title={
                      o.woId ? `Open products for W-${o.woId}` : undefined
                    }
                  >
                    <td
                      className="text-center"
                      // Ticking Shipped must not also open the modal.
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Marks the ORDER shipped, which writes to every bundle
                          on it -- the title says so, since the row this sits
                          on already represents the whole order. */}
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!o.shipped}
                        disabled={shippingOrderId === o.order_id || shippingGroup}
                        onChange={(e) => onToggleShipped(o, e.target.checked)}
                        title={
                          o.shipped && o.shippedAt
                            ? `Shipped ${new Date(o.shippedAt).toLocaleString()} — covers all ${o.bundleCount} bundle${o.bundleCount === 1 ? "" : "s"} on order ${o.order_id}`
                            : `Mark order ${o.order_id} shipped (all ${o.bundleCount} bundle${o.bundleCount === 1 ? "" : "s"})`
                        }
                        aria-label={`Mark order ${o.order_id} shipped`}
                      />
                    </td>
                    <td className="text-center text-nowrap">
                      {o.woId ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          {/* Opens the work order's products. */}
                          <button
                            type="button"
                            className="badge bg-success border-0"
                            // Bootstrap badges are 0.75em, which left the W-ID
                            // noticeably smaller than the row it identifies --
                            // and it is the thing people read off this table
                            // and say out loud. 14px matches the JOB NAME
                            // beside it.
                            style={{ fontSize: 14 }}
                            onClick={() => onOpenWorkOrder(o.woId)}
                            title={`Open work order W-${o.woId}`}
                          >
                            W-{o.woId}
                          </button>

                          {/* Shipping address history, for THIS order.
                              
                              Shown only when there is something to show:
                              addressHistoryCount is 0 for an address Nova
                              Shipping has never rewritten, and an icon there
                              would be a button that opens an empty box.
                              
                              On the row rather than inside the products modal,
                              where it read as if it belonged to the products
                              under it -- the history is a property of this
                              ORDER's address. */}
                          {o.addressHistoryCount > 0 && onOpenAddressHistory && (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-muted lh-1"
                              // Stops the ROW's click-to-open-products as well,
                              // which would otherwise fire underneath this and
                              // open two modals. Scoped to the button, not the
                              // cell: the row is meant to open products from
                              // anywhere except Shipped and the menu, and
                              // putting this on the whole cell silently killed
                              // that for the cell's empty space.
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenAddressHistory(o.woId);
                              }}
                              title={`Shipping address changed ${o.addressHistoryCount} time${
                                o.addressHistoryCount === 1 ? "" : "s"
                              } — see what it was`}
                              aria-label={`Shipping address history for W-${o.woId}`}
                            >
                              <i className="mdi mdi-history" aria-hidden="true" />
                            </button>
                          )}
                        </span>
                      ) : o.mappingNote ? (
                        <span className="badge bg-secondary">
                          {o.mappingNote}
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark">
                          Not set
                        </span>
                      )}
                    </td>
                    <td>
                      {o.jobName || (
                        // No work order means no job name -- say which it is
                        // rather than printing a bare dash.
                        <span className="text-muted fst-italic">
                          {o.woId ? "-" : "not linked"}
                        </span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {formatDueDate(o.dueDate) || (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {o.shipmentMethodName || (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      <span
                        className="badge bg-primary"
                        style={{ fontSize: 13 }}
                      >
                        {o.bundleCount}
                      </span>
                    </td>
                    <td
                      className="text-end text-nowrap"
                      // Opening the three-dot menu must not also open the
                      // products modal behind it.
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RowMenu order={o} onEditMapping={onEditMapping} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
